import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, STORAGE_KEYS } from '@/constants/app'
import { ROUTES } from '@/constants/routes'

export type LeadListPeriodShortcut = 'today' | 'week' | 'month' | 'year'

/** Follow-up tab only — filters by `nextFollowUpAt` (not createdAt period). */
export type LeadListFollowUpDue = 'today' | 'overdue'

/** List chrome tabs — Follow up is Queries-shaped (`stage=lead` + status follow_up). */
export type LeadListTab = 'lead' | 'follow' | 'registered'

/**
 * Server-side sort keys (Users `sortBy`/`sortDir` pattern — see
 * `salesLeadService.js#resolveLeadOrderBy`). Query/Follow-up and Registered
 * each only use their own subset; an unknown/cross-stage key falls back to
 * the default (`createdAt` desc) on the backend, never a 400/500.
 */
export type LeadListSortBy =
  | 'createdAt'
  | 'businessName'
  | 'name'
  | 'cityName'
  | 'status'
  | 'nextFollowUpAt'
  | 'totalQuantity'
  | 'product'
  | 'assignedTo'
  | 'leadNo'
  | 'email'
  | 'registeredStatus'
  | 'rlNo'
  | 'totalPrice'

export type LeadListSortDir = 'asc' | 'desc'

const LEAD_LIST_SORT_BY_VALUES: readonly LeadListSortBy[] = [
  'createdAt',
  'businessName',
  'name',
  'cityName',
  'status',
  'nextFollowUpAt',
  'totalQuantity',
  'product',
  'assignedTo',
  'leadNo',
  'email',
  'registeredStatus',
  'rlNo',
  'totalPrice',
]

export function parseLeadListSortBy(raw: string | null | undefined): LeadListSortBy {
  return (LEAD_LIST_SORT_BY_VALUES as readonly string[]).includes(raw ?? '')
    ? (raw as LeadListSortBy)
    : 'createdAt'
}

export function parseLeadListSortDir(raw: string | null | undefined): LeadListSortDir {
  return raw === 'asc' ? 'asc' : 'desc'
}

export interface LeadListCityOption {
  value: string
  label: string
  description?: string
}

/** Excel column free-text filters (API contains / queryRef). */
export interface LeadListColumnTextFilters {
  queryRef: string
  businessName: string
  name: string
  number: string
  email: string
  businessType: string
  product: string
  category: string
  quality: string
  color: string
  createdBy: string
  updatedBy: string
}

export interface LeadListFilterState extends LeadListColumnTextFilters {
  tab: LeadListTab
  search: string
  assignedUserIds: string[]
  status: string
  cityIds: string[]
  /** Labels for selected cities so MultiSelect trigger stays readable after URL restore. */
  cityOptions: LeadListCityOption[]
  /** Exact match on lead `regionSnapshot` (province display label). */
  region: string
  /** Sales territory UUID — singular API filter. */
  salesTerritoryId: string
  leadSources: string[]
  months: string[]
  dateFrom: string
  dateTo: string
  period: LeadListPeriodShortcut | null
  /** Follow-up tab only — `nextFollowUpAt` due window. Cleared on other tabs. */
  followUpDue: LeadListFollowUpDue | null
  /** Qty column total. Empty string means that side is open. */
  qtyFrom: string
  qtyTo: string
  /** Registered tab only. Empty means any. */
  quotes: '' | 'has' | 'none'
  /** true = seasonal only; false/undefined = all. */
  seasonal: boolean
  sortBy: LeadListSortBy
  sortDir: LeadListSortDir
  page: number
  pageSize: number
}

export function parseLeadListTab(raw: string | null | undefined): LeadListTab {
  if (raw === 'registered') return 'registered'
  if (raw === 'follow') return 'follow'
  return 'lead'
}

export function parseLeadListFollowUpDue(raw: string | null | undefined): LeadListFollowUpDue | null {
  if (raw === 'today' || raw === 'overdue') return raw
  return null
}

const PAGE_SIZES = new Set<number>(PAGE_SIZE_OPTIONS)

const COLUMN_TEXT_KEYS = [
  'queryRef',
  'businessName',
  'name',
  'number',
  'email',
  'businessType',
  'product',
  'category',
  'quality',
  'color',
  'createdBy',
  'updatedBy',
] as const satisfies readonly (keyof LeadListColumnTextFilters)[]

const COLUMN_TEXT_URL: Record<(typeof COLUMN_TEXT_KEYS)[number], string> = {
  queryRef: 'ref',
  businessName: 'company',
  name: 'contact',
  number: 'phone',
  email: 'email',
  businessType: 'bizType',
  product: 'product',
  category: 'category',
  quality: 'quality',
  color: 'color',
  createdBy: 'createdBy',
  updatedBy: 'updatedBy',
}

export function emptyColumnTextFilters(): LeadListColumnTextFilters {
  return {
    queryRef: '',
    businessName: '',
    name: '',
    number: '',
    email: '',
    businessType: '',
    product: '',
    category: '',
    quality: '',
    color: '',
    createdBy: '',
    updatedBy: '',
  }
}

function readColumnText(
  source: Partial<LeadListColumnTextFilters> | URLSearchParams,
): LeadListColumnTextFilters {
  const empty = emptyColumnTextFilters()
  if (source instanceof URLSearchParams) {
    for (const key of COLUMN_TEXT_KEYS) {
      empty[key] = source.get(COLUMN_TEXT_URL[key])?.trim() ?? ''
    }
    return empty
  }
  for (const key of COLUMN_TEXT_KEYS) {
    const value = source[key]
    empty[key] = typeof value === 'string' ? value : ''
  }
  return empty
}

function writeColumnText(sp: URLSearchParams, state: LeadListColumnTextFilters): void {
  for (const key of COLUMN_TEXT_KEYS) {
    const value = state[key]?.trim()
    if (value) sp.set(COLUMN_TEXT_URL[key], value)
  }
}

export function defaultLeadListFilters(): LeadListFilterState {
  return {
    tab: 'lead',
    search: '',
    assignedUserIds: [],
    status: 'all',
    cityIds: [],
    cityOptions: [],
    region: '',
    salesTerritoryId: '',
    leadSources: [],
    months: [],
    dateFrom: '',
    dateTo: '',
    period: null,
    followUpDue: null,
    qtyFrom: '',
    qtyTo: '',
    quotes: '',
    seasonal: false,
    sortBy: 'createdAt',
    sortDir: 'desc',
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    ...emptyColumnTextFilters(),
  }
}

function digitsOnly(value: unknown): string {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return String(value)
  if (typeof value !== 'string') return ''
  return value.replace(/[^\d]/g, '').slice(0, 9)
}

function parseQuotes(value: unknown): '' | 'has' | 'none' {
  return value === 'has' || value === 'none' ? value : ''
}

function csv(value: string | null): string[] {
  if (!value) return []
  return [...new Set(value.split(',').map((part) => part.trim()).filter(Boolean))]
}

/** Migrate older single city/source session shape into arrays. */
function normalizeStoredFilters(parsed: Partial<LeadListFilterState> & {
  cityId?: string
  cityLabel?: string
  cityDescription?: string
  leadSource?: string
}): LeadListFilterState {
  const defaults = defaultLeadListFilters()
  const cityIds =
    parsed.cityIds?.length
      ? parsed.cityIds
      : parsed.cityId
        ? [parsed.cityId]
        : []
  const cityOptions =
    parsed.cityOptions?.length
      ? parsed.cityOptions
      : parsed.cityId && parsed.cityLabel
        ? [
            {
              value: parsed.cityId,
              label: parsed.cityLabel,
              description: parsed.cityDescription,
            },
          ]
        : []
  const leadSources =
    parsed.leadSources?.length
      ? parsed.leadSources
      : parsed.leadSource
        ? [parsed.leadSource]
        : []
  return {
    ...defaults,
    ...parsed,
    ...readColumnText(parsed),
    tab: parseLeadListTab(typeof parsed.tab === 'string' ? parsed.tab : null),
    cityIds,
    cityOptions,
    leadSources,
    region: typeof parsed.region === 'string' ? parsed.region : '',
    salesTerritoryId: typeof parsed.salesTerritoryId === 'string' ? parsed.salesTerritoryId : '',
    followUpDue:
      parseLeadListTab(typeof parsed.tab === 'string' ? parsed.tab : null) === 'follow'
        ? parseLeadListFollowUpDue(
            typeof parsed.followUpDue === 'string' ? parsed.followUpDue : null,
          )
        : null,
    qtyFrom: digitsOnly(parsed.qtyFrom),
    qtyTo: digitsOnly(parsed.qtyTo),
    quotes: parseQuotes(parsed.quotes),
    seasonal: parsed.seasonal === true,
    sortBy: parseLeadListSortBy(typeof parsed.sortBy === 'string' ? parsed.sortBy : null),
    sortDir: parseLeadListSortDir(typeof parsed.sortDir === 'string' ? parsed.sortDir : null),
  }
}

export function parseLeadListSearchParams(sp: URLSearchParams): LeadListFilterState {
  const defaults = defaultLeadListFilters()
  const periodRaw = sp.get('period')
  const period: LeadListPeriodShortcut | null =
    periodRaw === 'today' ||
    periodRaw === 'week' ||
    periodRaw === 'month' ||
    periodRaw === 'year'
      ? periodRaw
      : null
  const page = Number(sp.get('page'))
  const pageSize = Number(sp.get('pageSize'))
  const cityIds = csv(sp.get('cities') || sp.get('city'))
  const cityLabels = csv(sp.get('cityLabels'))
  const cityRegions = csv(sp.get('cityRegions'))
  const cityOptions: LeadListCityOption[] = cityIds.map((id, index) => ({
    value: id,
    label: cityLabels[index] || id,
    description: cityRegions[index] || undefined,
  }))
  return {
    tab: parseLeadListTab(sp.get('tab')),
    search: sp.get('q')?.trim() ?? '',
    assignedUserIds: csv(sp.get('salesmen')),
    status: sp.get('status')?.trim() || 'all',
    cityIds,
    cityOptions,
    region: sp.get('region')?.trim() ?? '',
    salesTerritoryId: sp.get('territory')?.trim() ?? '',
    leadSources: csv(sp.get('sources') || sp.get('source')),
    months: csv(sp.get('months')),
    dateFrom: sp.get('from')?.trim() ?? '',
    dateTo: sp.get('to')?.trim() ?? '',
    period,
    followUpDue:
      parseLeadListTab(sp.get('tab')) === 'follow'
        ? parseLeadListFollowUpDue(sp.get('followUpDue'))
        : null,
    qtyFrom: digitsOnly(sp.get('qtyFrom')),
    qtyTo: digitsOnly(sp.get('qtyTo')),
    quotes: parseQuotes(sp.get('quotes')),
    seasonal: sp.get('seasonal') === '1' || sp.get('seasonal') === 'true',
    sortBy: parseLeadListSortBy(sp.get('sort')),
    sortDir: parseLeadListSortDir(sp.get('dir')),
    page: Number.isInteger(page) && page >= 1 ? page : defaults.page,
    pageSize: PAGE_SIZES.has(pageSize) ? pageSize : defaults.pageSize,
    ...readColumnText(sp),
  }
}

export function leadListSearchParams(state: LeadListFilterState): URLSearchParams {
  const sp = new URLSearchParams()
  // Always explicit: a bare /sales/leads opens the Summary tab (when permitted).
  sp.set('tab', state.tab)
  if (state.search.trim()) sp.set('q', state.search.trim())
  if (state.assignedUserIds.length) sp.set('salesmen', state.assignedUserIds.join(','))
  if (state.status && state.status !== 'all') sp.set('status', state.status)
  if (state.cityIds.length) {
    sp.set('cities', state.cityIds.join(','))
    const labels = state.cityIds.map(
      (id) => state.cityOptions.find((o) => o.value === id)?.label ?? '',
    )
    const regions = state.cityIds.map(
      (id) => state.cityOptions.find((o) => o.value === id)?.description ?? '',
    )
    if (labels.some(Boolean)) sp.set('cityLabels', labels.join(','))
    if (regions.some(Boolean)) sp.set('cityRegions', regions.join(','))
  }
  if (state.region.trim()) sp.set('region', state.region.trim())
  if (state.salesTerritoryId.trim()) sp.set('territory', state.salesTerritoryId.trim())
  if (state.leadSources.length) sp.set('sources', state.leadSources.join(','))
  if (state.months.length) sp.set('months', state.months.join(','))
  if (state.dateFrom) sp.set('from', state.dateFrom)
  if (state.dateTo) sp.set('to', state.dateTo)
  if (state.period) sp.set('period', state.period)
  if (state.tab === 'follow' && state.followUpDue) sp.set('followUpDue', state.followUpDue)
  if (state.qtyFrom) sp.set('qtyFrom', state.qtyFrom)
  if (state.qtyTo) sp.set('qtyTo', state.qtyTo)
  if (state.tab === 'registered' && state.quotes) sp.set('quotes', state.quotes)
  if (state.seasonal) sp.set('seasonal', '1')
  if (state.sortBy !== 'createdAt') sp.set('sort', state.sortBy)
  if (state.sortDir !== 'desc') sp.set('dir', state.sortDir)
  writeColumnText(sp, state)
  if (state.page > 1) sp.set('page', String(state.page))
  if (state.pageSize !== DEFAULT_PAGE_SIZE) sp.set('pageSize', String(state.pageSize))
  return sp
}

export function readStoredLeadListFilters(): LeadListFilterState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.SALES_LEAD_LIST_FILTERS)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<LeadListFilterState> & {
      cityId?: string
      cityLabel?: string
      cityDescription?: string
      leadSource?: string
    }
    return normalizeStoredFilters(parsed)
  } catch {
    return null
  }
}

export function writeStoredLeadListFilters(state: LeadListFilterState): void {
  try {
    sessionStorage.setItem(STORAGE_KEYS.SALES_LEAD_LIST_FILTERS, JSON.stringify(state))
  } catch {
    // Private mode / quota — URL params still persist on the list page itself.
  }
}

/** URL wins when it has any list keys; otherwise restore the last list visit. */
export function resolveLeadListFilters(sp: URLSearchParams): LeadListFilterState {
  const keys = [...sp.keys()]
  // `?tab=lead` alone (Queries tab link) restores the last visit like a bare URL did.
  if (keys.length === 1 && keys[0] === 'tab' && sp.get('tab') === 'lead') {
    const stored = readStoredLeadListFilters()
    if (stored) return { ...stored, tab: 'lead' }
  }
  if (keys.length > 0) return parseLeadListSearchParams(sp)
  return readStoredLeadListFilters() ?? defaultLeadListFilters()
}

export function salesLeadsListHref(overrides?: Partial<Pick<LeadListFilterState, 'tab'>>): string {
  const stored = readStoredLeadListFilters() ?? defaultLeadListFilters()
  const next = overrides ? { ...stored, ...overrides } : stored
  const query = leadListSearchParams(next).toString()
  return query ? `${ROUTES.SALES_LEADS}?${query}` : ROUTES.SALES_LEADS
}

